'use client'

import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'

import { validateEmail } from '@booking/lib/validation'
import { associateTicket } from '@booking/services/api'
import TextField from '@booking/components/FormFields/TextField'
import shared from '@booking/index.module.scss'
import styles from '../index.module.scss'

import type { AssociateLine } from '../lib'
import { useBookingExternal } from '../../../state/external.context'

type TicketRowProps = {
  cartId: string
  line: AssociateLine
  index: number
  ticketName: string
  existingEmails: string[]
  onCompleteChange?: (state: boolean, payload: { email: string }) => void
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

export default function TicketRow({
  cartId,
  line,
  index,
  ticketName,
  existingEmails,
  onCompleteChange = () => {},
}: TicketRowProps) {
  const t = useTranslations('booking')
  const { locale } = useBookingExternal()
  const didMount = useRef(false)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const normalizedEmail = normalizeEmail(email)

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      return
    }
    onCompleteChange(completed, { email: normalizedEmail })
  }, [completed, normalizedEmail, onCompleteChange])

  const hasNameError = !name.trim()
  const hasEmailError =
    !email.trim() || !validateEmail(email.trim()) || existingEmails.includes(normalizedEmail)
  const hasErrors = hasNameError || hasEmailError

  const lineId = line.id

  const handleAssociate = async () => {
    setSubmitted(true)
    setSubmitError(false)

    if (hasErrors) return
    if (!cartId || !lineId) {
      setSubmitError(true)
      return
    }

    setIsSubmitting(true)
    const response = await associateTicket(cartId, [
      { id: lineId, email: email.trim(), name: name.trim(), locale },
    ])
    setIsSubmitting(false)

    if (response == null || response === false) {
      setSubmitError(true)
      return
    }

    setCompleted(true)
  }

  const handleEdit = () => {
    setCompleted(false)
    setSubmitted(false)
    setSubmitError(false)
  }

  const handleEditKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleEdit()
    }
  }

  return (
    <div className={styles.row} data-uuid={lineId}>
      <div className={styles.ticketNumber}>
        {t('ticket.success.ticket')} {index + 1}
        {ticketName ? ` (${ticketName})` : ''}
      </div>

      <div className={styles.fields}>
        <TextField
          label={t('ticket.success.rfid.name')}
          value={name}
          className={styles.field}
          disabled={completed || isSubmitting}
          hasError={submitted && !completed && hasNameError}
          onChange={(event) => setName(event.target.value)}
        />

        <TextField
          label={t('ticket.success.rfid.email')}
          value={email}
          className={styles.field}
          disabled={completed || isSubmitting}
          hasError={submitted && !completed && hasEmailError}
          onChange={(event) => setEmail(event.target.value)}
        />

        <button
          type="button"
          className={cn(shared.button, styles.button)}
          disabled={completed || isSubmitting}
          onClick={() => void handleAssociate()}
        >
          {completed
            ? t('ticket.success.rfid.added')
            : isSubmitting
              ? t('ticket.payment.pleaseWait')
              : t('ticket.success.rfid.assign')}
        </button>
      </div>

      {submitError ? <div className={styles.errorText}>{t('ticket.payment.error')}</div> : null}

      {completed ? (
        <div className={styles.needsConfirm}>
          {t.rich('ticket.success.rfid.confirm', {
            button: (chunks) => (
              <span
                role="button"
                tabIndex={0}
                className={styles.edit}
                onClick={handleEdit}
                onKeyDown={handleEditKeyDown}
              >
                {chunks}
              </span>
            ),
          })}
        </div>
      ) : null}
    </div>
  )
}
